import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Box, Button, Card, CardContent, Grid, Stack, Typography } from "@mui/material";
import { fetchData } from "../../api/client";
import CampaignCard from "./components/CampaignCard";
import CampaignCreateWizardDialog from "./components/CampaignCreateWizardDialog";

function toFriendlyMessage(err) {
  const raw =
    (err && typeof err === "object" && (err.message || err.errorMessage || err.error)) ||
    (typeof err === "string" ? err : "") ||
    "";
  const status = err?.status ?? err?.response?.status ?? err?.response?.data?.status ?? null;
  if (status >= 500) return "Erro temporário no servidor. Tente novamente em instantes.";
  if (status === 401 || status === 403) return "Você não tem permissão para acessar este recurso.";
  if (status === 404) return "Recurso não encontrado.";
  return raw || "Não foi possível concluir a operação. Tente novamente.";
}

export default function CampaignsListPage() {
  const navigate = useNavigate();

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [wizardOpen, setWizardOpen] = useState(false);

  const refreshCampaigns = async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const res = await fetchData("/campaigns", "GET");
      setCampaigns(res?.data || []);
    } catch (err) {
      console.error("Erro ao carregar campanhas:", err);
      setErrorMsg(toFriendlyMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshCampaigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emptyState = useMemo(() => !loading && !campaigns.length, [campaigns.length, loading]);

  return (
    <Box p={4}>
      {errorMsg ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMsg}
        </Alert>
      ) : null}

      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2} mb={2} flexWrap="wrap" useFlexGap>
        <Box sx={{ minWidth: 0, maxWidth: { md: "min(100%, 42rem)" } }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 800 }}>
            Campanhas
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.5 }}>
            Centralize objetivo, canais e resultados. Cada campanha é um funil que você liga, pausa e acompanha aqui.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
          <Button variant="outlined" onClick={refreshCampaigns} disabled={loading}>
            Atualizar lista
          </Button>
          <Button variant="contained" onClick={() => setWizardOpen(true)} disabled={loading}>
            Nova campanha
          </Button>
        </Stack>
      </Stack>

      <Card variant="outlined" sx={{ mb: 2, bgcolor: "action.hover" }}>
        <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }} justifyContent="space-between">
            <Box>
              <Typography fontWeight={800} variant="subtitle1">
                Suas campanhas
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Cada card mostra objetivo, canal, status e os três indicadores — leads, conversões e receita — quando os dados estiverem disponíveis.
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: { sm: "nowrap" } }}>
              Legenda: Leads · Conversões · Receita
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        {(campaigns || []).map((c) => (
          <Grid key={c.id} item xs={12}>
            <CampaignCard
              campaign={c}
              onClick={() => navigate(`/campaigns/${c.id}`)}
              onToggleStatus={async () => {
                const next = c.status === "active" ? "paused" : "active";
                setErrorMsg("");
                try {
                  await fetchData(`/campaigns/${c.id}`, "PUT", { status: next });
                  await refreshCampaigns();
                } catch (err) {
                  console.error("Erro ao alternar status da campanha:", err);
                  setErrorMsg(toFriendlyMessage(err));
                }
              }}
            />
          </Grid>
        ))}

        {emptyState ? (
          <Grid item xs={12}>
            <Alert severity="info">
              Você ainda não tem campanhas. Use <strong>Nova campanha</strong> para montar o funil com um template — em poucos passos
              você já pode ativar e medir resultados.
            </Alert>
          </Grid>
        ) : null}
      </Grid>

      <CampaignCreateWizardDialog
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onCreated={(id) => {
          setWizardOpen(false);
          if (id) navigate(`/campaigns/${id}`);
          refreshCampaigns();
        }}
      />
    </Box>
  );
}

